import React, { useState, useEffect, useRef, useMemo, useId } from 'react';
import Flag from '../../Components/Helper/Flag';
import Icon from '../../Components/Helper/Icon';
import PropTypes from 'prop-types';
import classnames from 'classnames';

const CurrencyDropdownItem = (props) => {

    const {isoString, label, labelEN} = props.item;

    return (
        <div className="dropdown__value">
            { isoString ? <Flag name={isoString}/> : null} {isoString || label || labelEN}
        </div>
    );
};

CurrencyDropdownItem.propTypes = {
    item: PropTypes.shape({
        isoString: PropTypes.string,
        label: PropTypes.node,
        labelEN: PropTypes.string
    })
};

/**
 * This is a currency selector, displayed as a dropdown.
 * It's a custom implementation to avoid external dependencies and ensure consistency.
 */
const CurrencyDropdown = (props) => {
    const {
        className,
        data,
        value,
        onChange,
        valueField = 'isoString',
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        ...rest } = props;

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const listboxId = useId();
    const classNames = classnames('dropdown', className);

    const handleSelect = (item) => {
        if (onChange) {
            onChange(item);
        }
        setIsOpen(false);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen(!isOpen);
        }
    };

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const uniqueData = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];
        const map = new Map();
        data.forEach(item => {
            const key = item && item[valueField];
            if (key && !map.has(key)) {
                map.set(key, item);
            }
        });
        return Array.from(map.values());
    }, [data, valueField]);

    return (
        <div className={classNames} ref={dropdownRef}>
            <div
                className="rw-dropdownlist"
                onClick={() => setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                role="combobox"
                tabIndex="0"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                aria-controls={isOpen ? listboxId : undefined}
                {...rest}
            >
                <span className="rw-select">
                    <Icon name="chevron-down" />
                </span>
                {value ? (
                    <div className="rw-input">
                        <CurrencyDropdownItem item={value} />
                    </div>
                ) : <div className="rw-input">&nbsp;</div>}
            </div>
            {isOpen && (
            <div className="dropdown__options" role="listbox" id={listboxId}>
                {uniqueData.map((item, index) => (
                <div
                    key={item[valueField] || index}
                    className="dropdown__option"
                    onClick={() => handleSelect(item)}
                    role="option"
                    aria-selected={value && value[valueField] === item[valueField]}
                >
                    <CurrencyDropdownItem item={item} />
                </div>
                ))}
            </div>
            )}
        </div>
    );
};

CurrencyDropdown.propTypes = {
    data: PropTypes.array,
    value: PropTypes.object, // Can be null when no value is selected
    onChange: PropTypes.func,
    valueField: PropTypes.string,
     /** A descriptive ARIA label for accessibility. Required. */
    'aria-label': PropTypes.string.isRequired,
    /** The id of an element which labels the dropdown. */
    'aria-labelledby': PropTypes.string,
    textField: PropTypes.string,
};

export default CurrencyDropdown;